import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowUp, ArrowDown, CheckCircle2, Shield, Clock, FileText, Zap,
  BookOpen, Lock, Mail, Send, Loader2, Plus, AlertOctagon, Layers, Search,
  Calendar, Star, BarChart3, MessageSquare, Smartphone, BellRing, Building2,
  GitBranch, Workflow, FileLock2, Users, Bell,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Brand } from '@/components/ui/Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/* ─────────────────────────────────────── Lead Form ─────────────────────────────────────── */
const LeadForm = () => {
  const [form, setForm] = useState({ name: '', email: '', school: '', role: '', phone: '', message: '' });
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success('Thanks — our team will reach out shortly.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please email sales@schoolzest.com directly.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-emerald-200 p-10 shadow-sm" data-testid="lead-form-success">
        <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-['Outfit',sans-serif] font-bold text-[#0F1B36] mb-3">Request received</h3>
        <p className="text-slate-600 mb-3">Thanks for reaching out. Our team will reply within one working day.</p>
        <p className="text-slate-600">In the meantime, feel free to email us directly at <a href="mailto:sales@schoolzest.com" className="font-semibold text-emerald-700 hover:underline" data-testid="success-schoolzest-email">sales@schoolzest.com</a>.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm space-y-5" data-testid="lead-form">
      <div>
        <Label htmlFor="lead-name" className="text-slate-700 font-semibold">Your name <span className="text-red-500">*</span></Label>
        <Input id="lead-name" value={form.name} onChange={update('name')} placeholder="e.g. Mrs Tan Bee Lian" required className="mt-1.5" data-testid="lead-name-input" />
      </div>
      <div>
        <Label htmlFor="lead-email" className="text-slate-700 font-semibold">Work email <span className="text-red-500">*</span></Label>
        <Input id="lead-email" type="email" value={form.email} onChange={update('email')} placeholder="e.g. name@school.edu.sg" required className="mt-1.5" data-testid="lead-email-input" />
      </div>
      <div>
        <Label htmlFor="lead-school" className="text-slate-700 font-semibold">School</Label>
        <Input id="lead-school" value={form.school} onChange={update('school')} placeholder="e.g. Bedok North Secondary" className="mt-1.5" data-testid="lead-school-input" />
      </div>
      <div>
        <Label htmlFor="lead-message" className="text-slate-700 font-semibold">How can we help?</Label>
        <Textarea id="lead-message" rows={3} value={form.message} onChange={update('message')} placeholder="Pilot interest, school size, current relief workflow…" className="mt-1.5" data-testid="lead-message-input" />
      </div>

      {!showMore ? (
        <button type="button" onClick={() => setShowMore(true)} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid="lead-show-more">
          <Plus className="h-3 w-3" /> Add role &amp; phone (optional)
        </button>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-1 border-t border-slate-100">
          <div>
            <Label htmlFor="lead-role" className="text-slate-700 font-semibold">Role</Label>
            <Input id="lead-role" value={form.role} onChange={update('role')} placeholder="Vice-Principal, HOD…" className="mt-1.5" data-testid="lead-role-input" />
          </div>
          <div>
            <Label htmlFor="lead-phone" className="text-slate-700 font-semibold">Phone</Label>
            <Input id="lead-phone" value={form.phone} onChange={update('phone')} placeholder="+65 9123 4567" className="mt-1.5" data-testid="lead-phone-input" />
          </div>
        </div>
      )}

      <Button type="submit" disabled={submitting} className="bg-[#0F1B36] hover:bg-[#1E2A4F] text-white h-12 px-8 text-base font-bold rounded-lg w-full sm:w-auto" data-testid="lead-submit-button">
        {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-2 h-4 w-4" /> Request a Demo</>}
      </Button>
      <p className="text-xs text-slate-400">No pressure, no sales spam — just a friendly reply from a human. By submitting you agree to be contacted by Schoolzest about your enquiry.</p>
    </form>
  );
};

/* ────────────────────────────────────── Scroll Dock ────────────────────────────────────── */
const ScrollDock = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className={`fixed right-4 sm:right-6 bottom-6 z-40 flex flex-col gap-2 transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-testid="scroll-dock">
      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="h-11 w-11 rounded-full bg-slate-900 hover:bg-black text-white shadow-lg flex items-center justify-center transition-colors" aria-label="Scroll to top" data-testid="scroll-to-top">
        <ArrowUp className="h-5 w-5" />
      </button>
      <button onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })} className="h-11 w-11 rounded-full bg-slate-900 hover:bg-black text-white shadow-lg flex items-center justify-center transition-colors" aria-label="Scroll to bottom" data-testid="scroll-to-bottom">
        <ArrowDown className="h-5 w-5" />
      </button>
    </div>
  );
};

/* ────────────────────────────────── Hero Dashboard Card ────────────────────────────────── */
const HeroDashboardCard = () => (
  <div className="relative w-full max-w-xl mx-auto" data-testid="hero-dashboard-card">
    <div className="bg-slate-900 rounded-t-2xl px-3.5 py-2.5 flex items-center gap-3 shadow-2xl">
      <div className="flex gap-1.5">
        <div className="h-2.5 w-2.5 rounded-full bg-red-400/80"></div>
        <div className="h-2.5 w-2.5 rounded-full bg-amber-400/80"></div>
        <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/80"></div>
      </div>
      <div className="flex-1 text-center">
        <div className="bg-slate-800 text-slate-400 text-[10px] font-mono rounded px-3 py-1 inline-flex items-center gap-1.5">
          <Lock className="h-2.5 w-2.5" /> app.schoolzest.com / relief
        </div>
      </div>
      <div className="text-[10px] font-bold text-emerald-400 flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
        </span>
        06:47 AM
      </div>
    </div>
    <div className="bg-white rounded-b-2xl border-x border-b border-slate-200 shadow-2xl p-5 space-y-4">
      {/* Absence reported */}
      <div className="bg-rose-50/70 rounded-xl border border-rose-100 p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-rose-600 mb-1.5">Absence Reported</p>
        <p className="font-bold text-[#0F1B36]">Mrs Lim Bee Cheng</p>
        <p className="text-xs text-slate-500 mt-0.5">HOD English &middot; MC &middot; 4 periods</p>
        <span className="inline-block mt-2 text-[10px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">06:43 AM</span>
      </div>
      {/* Best match */}
      <div className="bg-emerald-50/70 rounded-xl border border-emerald-200 p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1.5">Best Match &middot; Period 2</p>
        <p className="font-bold text-[#0F1B36]">Mr Nair Suresh</p>
        <p className="text-xs text-slate-500 mt-0.5">English &middot; Free P2 &middot; 2/8 duties</p>
        <span className="inline-block mt-2 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Score 97</span>
      </div>
      {/* Assign + Notify CTA */}
      <div className="bg-[#0F1B36] rounded-xl p-4 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-300">Assign <span className="font-bold text-white">Mr Nair</span> &rarr; <span className="font-bold text-white">3E2 P2</span></p>
        <button className="bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 shrink-0 transition-colors">
          <CheckCircle2 className="h-3.5 w-3.5" /> Assign + Notify
        </button>
      </div>
    </div>
  </div>
);

/* ────────────────────────────── Beat 1 — Full Dashboard Mockup ────────────────────────── */
const FullDashboardMockup = () => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200" data-testid="beat1-dashboard">
    {/* Chrome */}
    <div className="bg-slate-900 px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5">
        <div className="h-2.5 w-2.5 rounded-full bg-red-400/80"></div>
        <div className="h-2.5 w-2.5 rounded-full bg-amber-400/80"></div>
        <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/80"></div>
      </div>
      <div className="flex-1 text-center">
        <div className="bg-slate-800 text-slate-400 text-[10px] font-mono rounded px-3 py-1 inline-flex items-center gap-1.5">
          <Lock className="h-2.5 w-2.5" /> app.schoolzest.com / relief / today
        </div>
      </div>
      <div className="text-[10px] font-bold text-emerald-400 flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
        </span>
        Mon 2 Jun &middot; 06:47 AM
      </div>
    </div>

    <div className="grid grid-cols-12 bg-slate-50 min-h-[560px]">
      {/* Sidebar */}
      <aside className="hidden md:block md:col-span-3 bg-[#0F1B36] text-slate-300 p-5">
        <div className="mb-6">
          <p className="text-base font-black font-['Outfit',sans-serif] text-white">School<span className="text-emerald-400">Zest</span></p>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mt-1">Bedok North Secondary</p>
        </div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-2">Operations</p>
        <div className="space-y-1 mb-5">
          {[
            { icon: Zap, label: 'Relief Today', active: true },
            { icon: Calendar, label: 'Timetable' },
            { icon: Star, label: 'Invigilation' },
          ].map((l) => {
            const Icon = l.icon;
            return (
              <div key={l.label} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${l.active ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20' : 'text-slate-400'}`}>
                <Icon className="h-3.5 w-3.5" /> {l.label}
              </div>
            );
          })}
        </div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-2">Reports</p>
        <div className="space-y-1">
          {[
            { icon: BarChart3, label: 'Workload' },
            { icon: FileLock2, label: 'Audit Log' },
          ].map((l) => {
            const Icon = l.icon;
            return (
              <div key={l.label} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-400">
                <Icon className="h-3.5 w-3.5" /> {l.label}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Main */}
      <main className="col-span-12 md:col-span-6 bg-white p-5 sm:p-6 border-r border-slate-100">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <p className="text-base font-black text-[#0F1B36] font-['Outfit',sans-serif] leading-tight">Relief Assignment — Monday</p>
            <p className="text-xs text-slate-500 mt-0.5">2 June 2025 &middot; 3 periods require coverage</p>
          </div>
          <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full flex items-center gap-1 shrink-0">
            <Zap className="h-3 w-3" /> 3 Unassigned
          </span>
        </div>

        {/* Absence card */}
        <div className="bg-rose-50/60 border border-rose-100 rounded-xl p-3.5 flex items-center gap-3 mb-5">
          <div className="h-9 w-9 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
            <AlertOctagon className="h-4.5 w-4.5 text-rose-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#0F1B36] truncate">Mrs Lim Bee Cheng — MC reported</p>
            <p className="text-[11px] text-slate-500 truncate">HOD English &middot; Periods 2, 3, 5, 7 affected</p>
          </div>
          <span className="text-[10px] font-bold text-slate-400 font-mono shrink-0">06:43 AM</span>
        </div>

        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Affected Periods</p>
        <div className="grid grid-cols-4 gap-2 mb-5">
          {[
            { p: 'P2', t: '08:00', cls: '3E2', sub: 'English', st: 'Unassigned', done: false },
            { p: 'P3', t: '09:00', cls: '3E2', sub: 'English', st: 'Assigned', done: true },
            { p: 'P5', t: '11:00', cls: '4N1', sub: 'English', st: 'Unassigned', done: false },
            { p: 'P7', t: '13:30', cls: '2E3', sub: 'Lit/Eng', st: 'Unassigned', done: false },
          ].map((s) => (
            <div key={s.p} className={`rounded-lg p-2 border ${s.done ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50/60 border-amber-200'}`}>
              <p className="text-[10px] font-bold text-[#0F1B36]">{s.p} &middot; {s.t}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{s.cls}</p>
              <p className="text-[9px] text-slate-400 truncate">{s.sub}</p>
              <p className={`text-[9px] font-bold mt-1 ${s.done ? 'text-emerald-700' : 'text-amber-700'}`}>{s.done && <CheckCircle2 className="inline h-2.5 w-2.5 mr-0.5" />}{s.st}</p>
            </div>
          ))}
        </div>

        {/* Engine */}
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-xs font-bold text-[#0F1B36]">Auto-Assignment &middot; Period 2 &rarr; Class 3E2</p>
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">Smart Ranked</span>
        </div>

        {/* Match cards */}
        <div className="space-y-2 mb-4">
          {[
            { initials: 'MN', name: 'Mr Nair Suresh', chips: [{ l: 'Free P2', c: 'emerald' }, { l: 'English', c: 'indigo' }, { l: '2/8 duties', c: 'slate' }], score: 97, best: true },
            { initials: 'TY', name: 'Ms Tan Yu Ling', chips: [{ l: 'Free P2', c: 'emerald' }, { l: '4/8 duties', c: 'slate' }], score: 81 },
            { initials: 'CJ', name: 'Mr Chan Jun Wei', chips: [{ l: 'Free P2', c: 'emerald' }, { l: '5/8 duties', c: 'slate' }], score: 64 },
          ].map((m) => (
            <div key={m.initials} className={`relative rounded-xl border p-3 flex items-center gap-3 ${m.best ? 'bg-emerald-50 border-emerald-400 shadow-sm' : 'bg-white border-slate-200'}`}>
              {m.best && <span className="absolute -top-2 left-3 text-[9px] font-black tracking-widest text-emerald-700 bg-white border border-emerald-300 px-1.5 py-0.5 rounded">BEST MATCH</span>}
              <div className={`h-9 w-9 rounded-full ${m.best ? 'bg-emerald-600' : 'bg-slate-600'} text-white text-xs font-bold flex items-center justify-center shrink-0`}>{m.initials}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#0F1B36] truncate">{m.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {m.chips.map((c) => (
                    <span key={c.l} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      c.c === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                      c.c === 'indigo' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                    }`}>{c.l}</span>
                  ))}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-xl font-black leading-none ${m.best ? 'text-emerald-600' : 'text-slate-400'}`}>{m.score}</p>
                <p className="text-[9px] text-slate-400 uppercase tracking-wider">score</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold py-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors">
            <CheckCircle2 className="h-3.5 w-3.5" /> Assign Mr Nair — Notify via WhatsApp
          </button>
          <button className="border border-slate-300 text-slate-600 text-xs font-bold px-4 py-2.5 rounded-lg hover:bg-slate-50 transition-colors">Override</button>
        </div>
      </main>

      {/* Right panel */}
      <aside className="hidden md:block md:col-span-3 bg-white p-5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Relief Workload &middot; This Term</p>
        <div className="space-y-2.5 mb-5">
          {[
            { name: 'Mr Nair Suresh', cnt: '2/8', pct: 25, tone: 'emerald' },
            { name: 'Ms Tan Yu Ling', cnt: '4/8', pct: 50, tone: 'amber' },
            { name: 'Mr Chan Jun Wei', cnt: '5/8', pct: 63, tone: 'amber' },
            { name: 'Mrs Priya Rajan', cnt: '7/8', pct: 88, tone: 'rose' },
            { name: 'Mr Yusof Hakim', cnt: '3/8', pct: 38, tone: 'emerald' },
          ].map((w) => (
            <div key={w.name}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold text-slate-700 truncate">{w.name}</span>
                <span className="text-[10px] font-bold text-slate-500 font-mono">{w.cnt}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${w.tone === 'emerald' ? 'bg-emerald-500' : w.tone === 'amber' ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${w.pct}%` }}></div>
              </div>
            </div>
          ))}
        </div>

        <hr className="border-slate-100 my-4" />

        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Audit Log &middot; Today</p>
        <div className="space-y-2.5 mb-4">
          {[
            { dot: 'emerald', body: <><strong>P3 assigned</strong> &rarr; Mr Yusof Hakim</>, ts: '06:51 AM' },
            { dot: 'amber', body: <><strong>Absence logged</strong> &middot; Mrs Lim BC</>, ts: '06:43 AM' },
            { dot: 'slate', body: <><strong>Daily briefing sent</strong> &middot; 42 staff</>, ts: '06:30 AM' },
          ].map((a, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${a.dot === 'emerald' ? 'bg-emerald-500' : a.dot === 'amber' ? 'bg-amber-500' : 'bg-slate-400'}`}></div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-700 leading-tight">{a.body}</p>
                <p className="text-[9px] text-slate-400 font-mono mt-0.5">{a.ts}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-5 w-5 rounded bg-emerald-500 flex items-center justify-center">
              <MessageSquare className="h-3 w-3 text-white" />
            </div>
            <span className="text-[10px] font-bold text-emerald-700 flex-1">WhatsApp Sent</span>
            <span className="text-[9px] text-emerald-600 font-mono">06:51</span>
          </div>
          <p className="text-[11px] text-emerald-900 leading-snug">&ldquo;Hi <strong>Mr Yusof</strong>, you&apos;ve been assigned relief for <strong>3E2 &middot; P3 &middot; 09:00</strong>. Materials attached.&rdquo;</p>
        </div>
      </aside>
    </div>
  </div>
);

/* ─────────────────────────────────────── Pain Card ─────────────────────────────────────── */
const PainCard = ({ icon: Icon, title, body }) => (
  <div className="flex flex-col">
    <div className="h-10 w-10 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-5">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="text-xl font-bold mb-2 text-white leading-tight">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
  </div>
);

/* ─────────────────────────────────────── Main Page ─────────────────────────────────────── */
const LandingPage = () => {
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Outfit:wght@400;500;600;700;800;900&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Manrope',sans-serif] text-slate-900 selection:bg-emerald-500/30">
      <ScrollDock />

      {/* ─── Nav ─── */}
      <nav className="sticky top-0 w-full backdrop-blur-xl bg-white/70 border-b border-slate-200/50 z-40">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brand theme="light" />
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <a href="#intelligence" className="hover:text-emerald-700 transition-colors" data-testid="nav-intelligence-link">How it works</a>
            <a href="#pilot" className="hover:text-emerald-700 transition-colors" data-testid="nav-pilot-link">Pilot Cohort</a>
            <a href="#faq" className="hover:text-emerald-700 transition-colors" data-testid="nav-faq-link">FAQ</a>
            <a href="#contact" className="hover:text-emerald-700 transition-colors" data-testid="nav-contact-link">Contact</a>
          </div>
          <Button
            onClick={() => { const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            className="bg-[#0F1B36] hover:bg-[#1E2A4F] text-white rounded-md px-6 shadow-sm"
            data-testid="nav-trial-button"
          >
            Book a Demo
          </Button>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative pt-20 pb-0 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 flex flex-col items-start z-10">
            <div className="inline-flex items-center gap-2.5 mb-7">
              <span className="h-px w-8 bg-emerald-600"></span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">Scheduling Software for Singapore Schools</span>
            </div>
            <h1 className="font-['Outfit',sans-serif] text-5xl sm:text-6xl font-black text-[#0F1B36] leading-[1.05] tracking-tight mb-7">
              Your relief roster,<br />
              <em className="not-italic text-emerald-700">done while you commute.</em>
            </h1>
            <p className="text-lg text-slate-600 mb-9 leading-relaxed max-w-lg">
              Built for Singapore MOE schools. SchoolZest automates relief scheduling, timetabling, and exam invigilation — so your morning starts with answers, not WhatsApp threads.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button
                onClick={() => { const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="bg-[#0F8A5F] hover:bg-[#0c704c] text-white h-14 px-8 text-base font-bold rounded-lg shadow-lg shadow-emerald-500/20"
                data-testid="hero-demo-button"
              >
                Book a Free Demo <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => { const el = document.getElementById('intelligence'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="h-14 px-8 border-slate-300 text-[#0F1B36] hover:bg-slate-50 text-base font-bold rounded-lg"
                data-testid="hero-howitworks-button"
              >
                See how it works <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6 relative z-0 pb-12 lg:pb-0">
            <HeroDashboardCard />
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-emerald-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 z-[-1]"></div>
            <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-[#C9A23E] rounded-full mix-blend-multiply filter blur-3xl opacity-15 z-[-1]"></div>
          </div>
        </div>

        {/* Trust bar inside hero */}
        <div className="mt-20 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-center">
            {[
              { icon: Shield, label: 'Singapore-Hosted' },
              { icon: Lock, label: 'PDPA Compliant' },
              { icon: Building2, label: 'Built for MOE School Structures' },
            ].map((b, i, arr) => {
              const Icon = b.icon;
              return (
                <React.Fragment key={b.label}>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-xs font-bold uppercase tracking-wider">{b.label}</span>
                  </div>
                  {i < arr.length - 1 && <span className="hidden sm:inline text-slate-300">·</span>}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Pain ─── */}
      <section id="pain" className="py-28 bg-slate-900 text-white border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-14">
            <div className="inline-flex items-center gap-2.5 mb-5">
              <span className="h-px w-8 bg-rose-500"></span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-rose-400">The Problem</span>
            </div>
            <h2 className="font-['Outfit',sans-serif] text-4xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl">
              Spreadsheets and WhatsApp<br />were never built for this.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10">
            <PainCard icon={Clock} title="Manual Scramble" body="Waking up at 5:30 AM to find replacements when a teacher calls in sick." />
            <PainCard icon={ArrowDown} title="Unfair Allocation" body="The same teachers take every extra duty. Everyone knows it. Nobody says anything." />
            <PainCard icon={MessageSquare} title="Lost Lesson Materials" body="Instructions sent via WhatsApp at 6 AM. The relief teacher saw it. Or didn't. No way to know until the lesson starts." />
            <PainCard icon={Layers} title="Timetable Silos" body="A teacher gets assigned relief for Period 3. Nobody checked she already has a class. The class finds out before admin does." />
            <PainCard icon={Search} title="No Audit Trail" body="A parent calls to ask why their child's class had no teacher for two periods. You have no record of what happened or who was responsible." />
            <PainCard icon={FileText} title="Exam Day Chaos" body="Every exam season, the invigilation roster gets rebuilt from scratch. Same conflicts. Same complaints. Same week-before panic." />
          </div>
        </div>
      </section>

      {/* ─── Intelligence Layer ─── */}
      <section id="intelligence" className="bg-slate-50">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-16 text-center">
          <div className="inline-flex items-center gap-2.5 mb-5">
            <span className="h-px w-8 bg-emerald-600"></span>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">The Intelligence Layer</span>
            <span className="h-px w-8 bg-emerald-600"></span>
          </div>
          <h2 className="font-['Outfit',sans-serif] text-4xl md:text-5xl lg:text-6xl font-black text-[#0F1B36] tracking-tight leading-[1.05] mb-6">
            The <em className="not-italic text-emerald-700">smarter</em> way to run<br />school operations.
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From morning absence to confirmed coverage — no manual coordination, no group messages, no spreadsheets.
          </p>
        </div>

        {/* Beat 1 — The Product in Action */}
        <div className="relative bg-gradient-to-b from-slate-100 to-slate-50 border-y border-slate-200 py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-8">
              <span className="text-[10px] font-black tracking-widest text-emerald-700 bg-white border border-emerald-200 px-2 py-1 rounded">01</span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">The Product in Action</span>
            </div>
            <FullDashboardMockup />

            {/* Annotation notes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {[
                { num: '01', label: 'Absence logged 06:43 AM', title: 'Timetable cross-referenced instantly', body: 'MC reported. SchoolZest identifies every affected period, class, and subject — no manual checking required.' },
                { num: '02', label: 'Smart matching engine', title: 'Best match surfaced in seconds', body: 'Free period, subject fit, relief quota, and workload balance — all factored. The HOD sees ranked options, not a raw list.' },
                { num: '03', label: 'One click', title: 'Assigned. Notified. Logged.', body: 'Teacher notified via WhatsApp, lesson materials attached, and every action written to a tamper-evident audit trail.' },
              ].map((n) => (
                <div key={n.num} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-2">{n.num} — {n.label}</p>
                  <h4 className="text-base font-bold text-[#0F1B36] mb-2 leading-snug">{n.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{n.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Beat 2 — Core Capabilities */}
        <div className="py-24 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-8">
              <span className="text-[10px] font-black tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">02</span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Core Capabilities</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
              <div className="lg:col-span-7">
                <h3 className="font-['Outfit',sans-serif] text-3xl md:text-4xl font-black text-[#0F1B36] tracking-tight leading-[1.1]">
                  Three things SchoolZest<br />does <em className="not-italic text-emerald-700">exceptionally</em> well.
                </h3>
              </div>
              <div className="lg:col-span-5 flex items-end">
                <p className="text-base text-slate-600 leading-relaxed">
                  Not a feature catalogue. The three capabilities built specifically around how Singapore schools operate day to day.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Zap, title: 'Relief Scheduling', body: 'From absence reported to teacher assigned — in under two minutes. Quota-aware, subject-aware, workload-balanced. No more calling around or group messages.', tag: 'Fairness enforced by design' },
                { icon: FileText, title: 'Exam Invigilation', body: 'Invigilation rosters built automatically. Break rules, consecutive duty limits, and venue constraints respected from the start. Every exam season, in minutes.', tag: 'Auto-roster with constraint logic' },
                { icon: Calendar, title: 'Timetable Integration', body: 'Your existing timetable syncs directly. No double-booking. No teacher assigned to two classes. Conflicts caught before they reach the classroom.', tag: 'API sync with existing systems' },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="relative bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all overflow-hidden">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-emerald-500 to-emerald-300"></div>
                    <div className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-5">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h4 className="text-xl font-bold text-[#0F1B36] mb-3">{c.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5">{c.body}</p>
                    <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                      <span className="h-px w-6 bg-emerald-500"></span>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{c.tag}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Channels row */}
            <div className="mt-12 bg-slate-50 rounded-2xl border border-slate-200 p-6 flex flex-col lg:flex-row items-start lg:items-center gap-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 shrink-0">Notifies via</p>
              <div className="flex flex-wrap gap-2 flex-1">
                {[
                  { label: 'WhatsApp', color: 'text-[#25D366]', icon: MessageSquare },
                  { label: 'Telegram', color: 'text-[#2AABEE]', icon: Send },
                  { label: 'SMS', color: 'text-slate-500', icon: Smartphone },
                  { label: 'Email', color: 'text-rose-500', icon: Mail },
                  { label: 'Push', color: 'text-violet-500', icon: BellRing },
                ].map((ch) => {
                  const Icon = ch.icon;
                  return (
                    <div key={ch.label} className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#0F1B36]">
                      <Icon className={`h-3.5 w-3.5 ${ch.color}`} /> {ch.label}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 leading-snug max-w-[240px] lg:text-right lg:ml-auto">Teachers get notified on the channel they already use. No behaviour change required from staff.</p>
            </div>
          </div>
        </div>

        {/* Beat 3 — Trust & Compliance */}
        <div className="py-24 bg-[#0F1B36] text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-8">
                  <span className="text-[10px] font-black tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded">03</span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Trust &amp; Compliance</span>
                </div>
                <h3 className="font-['Outfit',sans-serif] text-3xl md:text-4xl font-black tracking-tight leading-[1.1] mb-5">
                  Built for Singapore schools<br />from day one.
                </h3>
                <p className="text-base text-slate-400 leading-relaxed">
                  Not a generic HR tool adapted for schools. SchoolZest is designed around MOE school structures, Singapore&apos;s data protection requirements, and the operational reality of a school admin&apos;s morning.
                </p>
              </div>

              <div className="lg:col-span-7 space-y-4">
                {[
                  { icon: Shield, title: 'Singapore-Hosted Data', body: 'All school and staff data resides on Singapore servers. No cross-border transfers. Full visibility of where your data sits.' },
                  { icon: Lock, title: 'PDPA Compliant', body: 'Staff personal data handled in strict accordance with Singapore\u2019s Personal Data Protection Act. Role-based access means staff only see what they need to.', tag: 'Admin · HOD · Teacher · EXCO' },
                  { icon: FileLock2, title: 'Tamper-Evident Audit Trail', body: 'Every assignment, change, and override is logged with a timestamp and user ID. When a parent asks why their child\u2019s class was unattended, you have the answer.', tag: 'SHA-256 Hash Chain · MFA · Brute-Force Lock' },
                  { icon: Building2, title: 'MOE School Structure', body: 'Period formats, academic calendar, partial-day absences, and Express/Normal/IP stream logic built in. No configuration required to reflect how your school actually runs.' },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <div key={t.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-start gap-5 hover:bg-white/10 transition-colors">
                      <div className="h-11 w-11 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h5 className="text-base font-bold text-white mb-1.5">{t.title}</h5>
                        <p className="text-sm text-slate-400 leading-relaxed">{t.body}</p>
                        {t.tag && (
                          <span className="inline-block mt-3 text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded">{t.tag}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-center text-slate-400 text-sm max-w-2xl mx-auto mt-14 italic">
              Everything above is verifiable. We&apos;ll show you the audit log, the data hosting, and the PDPA documentation in the demo.
            </p>

            {/* Closing strip */}
            <div className="mt-10 bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              <p className="text-sm sm:text-base text-slate-200 leading-snug">
                Ready to see it in your school&apos;s context? <em className="not-italic text-emerald-300 font-semibold">A 30-minute demo. Your timetable structure. Your questions.</em>
              </p>
              <Button
                onClick={() => { const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="bg-emerald-500 hover:bg-emerald-400 text-white h-12 px-6 text-sm font-bold rounded-lg shrink-0"
                data-testid="intelligence-cta-button"
              >
                Book a Free Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pilot Cohort ─── */}
      <section id="pilot" className="py-24 bg-emerald-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-100 mb-5">Founding School Programme</p>
          <h2 className="font-['Outfit',sans-serif] text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-6">
            Join our pilot cohort.<br />Limited to 5 schools.
          </h2>
          <p className="text-base sm:text-lg text-emerald-50 max-w-2xl mx-auto mb-10 leading-relaxed">
            Selected schools get full access at no cost for one semester. We handle all setup and onboarding. In return, we ask for honest feedback and the opportunity to document the outcome.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-3xl mx-auto mb-10">
            {[
              { n: 'School 1', state: 'Taken' },
              { n: 'School 2', state: 'Open' },
              { n: 'School 3', state: 'Open' },
              { n: 'School 4', state: 'Open' },
              { n: 'School 5', state: 'Open' },
            ].map((s) => (
              <div
                key={s.n}
                className={`rounded-xl px-4 py-4 text-xs font-bold ${
                  s.state === 'Taken'
                    ? 'bg-white/10 border border-white/20 text-emerald-200 line-through'
                    : 'bg-white text-emerald-700 border border-white'
                }`}
                data-testid={`pilot-slot-${s.n.toLowerCase().replace(' ', '-')}`}
              >
                <p className="leading-tight">{s.n}</p>
                <p className={`text-[10px] mt-1 ${s.state === 'Taken' ? 'text-emerald-300/60' : 'text-emerald-600'}`}>{s.state}</p>
              </div>
            ))}
          </div>

          <Button
            onClick={() => { const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            className="bg-white text-emerald-700 hover:bg-emerald-50 h-14 px-8 text-base font-bold rounded-lg shadow-xl"
            data-testid="pilot-apply-button"
          >
            Apply for a Pilot Slot <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-28 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12 text-center">
            <p className="font-['Outfit',sans-serif] text-sm font-bold tracking-[0.2em] text-emerald-700 uppercase mb-4">FAQ</p>
            <h2 className="font-['Outfit',sans-serif] text-4xl md:text-5xl font-black text-[#0F1B36] tracking-tight">
              Questions principals and HODs typically ask.
            </h2>
          </div>

          <div className="space-y-3">
            {[
              {
                q: 'Is this MOE-approved or endorsed?',
                a: 'SchoolZest is an independent product. It is not an MOE-mandated system, and schools are not required to seek MOE approval to evaluate or pilot scheduling software for internal administrative use. We recommend checking with your cluster superintendent if you have specific procurement guidelines to follow.',
              },
              {
                q: 'How long does setup take?',
                a: 'Most schools are operational within one week. We handle the onboarding: timetable import, staff account setup, and a 90-minute admin training session. No IT deployment required — SchoolZest runs entirely in the browser.',
              },
              {
                q: 'What does it cost?',
                a: 'Pilot schools participate at no cost for the first semester. Post-pilot pricing is based on school size and structured as an annual subscription. We will discuss pricing transparently before any commitment is required.',
              },
              {
                q: 'Does it integrate with our existing timetable software?',
                a: 'SchoolZest accepts timetable imports in standard spreadsheet formats (Excel, CSV). If your school uses a specific timetabling tool, let us know during the demo and we will confirm compatibility before you commit.',
              },
              {
                q: 'Who in the school needs to use it?',
                a: 'Primarily the admin manager or designated HOD who coordinates relief. Teachers only interact with SchoolZest when notified of an assignment — no training required for general staff. The principal has a read-only oversight view.',
              },
            ].map((item, i) => (
              <details key={i} className="group bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer px-6 py-5 flex items-center justify-between gap-4 list-none">
                  <h3 className="text-base font-bold text-[#0F1B36]">{item.q}</h3>
                  <Plus className="h-5 w-5 text-slate-400 group-open:rotate-45 transition-transform shrink-0" />
                </summary>
                <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Contact ─── */}
      <section id="contact" className="py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="font-['Outfit',sans-serif] text-sm font-bold tracking-[0.2em] text-emerald-700 uppercase mb-4">Book a Free Demo</p>
            <h2 className="font-['Outfit',sans-serif] text-4xl font-black text-[#0F1B36] tracking-tight mb-4">
              30 minutes. Your school&apos;s context.
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              We&apos;ll show you the system using your timetable structure and answer your questions. No obligation. We reply within one working day.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-4">What you&apos;ll see in the demo</p>
                <ul className="space-y-3 text-sm text-slate-700">
                  {[
                    'A live walk-through of relief auto-assignment',
                    'Exam invigilation roster generated end-to-end',
                    'Timetable import from your existing format',
                    'Audit log, PDPA documentation, and data hosting',
                    'Q&A with the founding team',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Prefer email?</p>
                <a href="mailto:sales@schoolzest.com" className="group flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors" data-testid="contact-schoolzest">
                  <div className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 group-hover:bg-emerald-700 transition-colors">
                    <Mail className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-bold text-[#0F1B36]">sales@schoolzest.com</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-[#0F1B36] text-slate-300 py-16 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2">
            <div className="text-white mb-6">
              <Brand theme="dark" />
            </div>
            <p className="text-slate-400 max-w-sm mb-6">
              Built for Singapore Schools — solving the everyday problems Singapore teachers face.
            </p>
            <a href="mailto:sales@schoolzest.com" className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors" data-testid="footer-contact-schoolzest">
              <Mail className="h-4 w-4" /> sales@schoolzest.com
            </a>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-sm">Product</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#intelligence" className="hover:text-white transition-colors" data-testid="footer-howitworks">How it works</a></li>
              <li><a href="#pilot" className="hover:text-white transition-colors" data-testid="footer-pilot">Pilot Cohort</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors" data-testid="footer-faq">FAQ</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-sm">Company</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="mailto:sales@schoolzest.com" className="hover:text-white transition-colors" data-testid="footer-schoolzest-email">sales@schoolzest.com</a></li>
              <li>
                <a href="https://ten-gb.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-tengb-link">
                  About Ten-GB
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <p data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} SchoolZest. A product by{' '}
            <a href="https://ten-gb.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-400 hover:text-emerald-300 transition-colors">Ten-GB</a>.
          </p>
          <div className="flex gap-4">
            <span>Built for Singapore Schools</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
